import { Fragment, useMemo } from 'react';

import { useDatafeedSearchCauses } from '@givebacks/api/datafeed';
import { useNavigate } from 'react-router-dom';
import { CauseDetail, CauseStoreCard, Divider, Group, Skeleton, Stack, Text, ThemeIcon } from 'ui';
import { useDebouncedValue } from 'ui/Hooks';
import { IconVerified } from 'ui/Icons';

const CauseSearchResultView = ({ causeSearch }: { causeSearch?: string | null }) => {
  const navigate = useNavigate();
  const [debouncedValue] = useDebouncedValue(causeSearch, 250);

  const { data: causes, isFetching } = useDatafeedSearchCauses({
    search: debouncedValue ? encodeURIComponent(debouncedValue) : undefined,
    keepPreviousData: true,
  });

  const isLoadingWithNoResults = useMemo(() => {
    return Boolean(causes && causes?.length === 0 && isFetching);
  }, [isFetching, causes]);

  return (
    <Stack>
      <Group justify="space-between">
        <Text fz="14px" c="gray.6" fw={600}>
          Search Results
        </Text>
        <Group gap="4">
          <Text c="cobalt.5" fz="12px" fw={600}>
            Active on Givebacks{' '}
          </Text>
          <ThemeIcon color="cobalt.5" variant="transparent" size="sm">
            <IconVerified size="16px" />
          </ThemeIcon>
        </Group>
      </Group>
      {isLoadingWithNoResults && (
        <Stack>
          <Skeleton height={100} />
          <Skeleton height={100} />
          <Skeleton height={100} />
        </Stack>
      )}
      {causes?.map((cause) => (
        <Fragment key={cause.uuid}>
          <CauseStoreCard
            key={cause.uuid}
            direction="column"
            imgSrc={cause.thumbnail}
            title={cause.name}
            onClick={() => navigate(`/onboarding/causes/${cause.uuid}`)}
            info={{
              detail: (
                <CauseDetail
                  city={cause?.city}
                  state={cause?.state}
                  website={cause?.website}
                  withCheckmark={cause.stripe_connected}
                />
              ),
            }}
            w="100%"
          />
          <Divider />
        </Fragment>
      ))}
    </Stack>
  );
};

export { CauseSearchResultView };
